import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { User } from 'src/app/calvin-emr-shared/models/user.model';
import { UserService } from 'src/app/calvin-emr-shared/services/user.service';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { Router, RouterLink } from '@angular/router';
import { SUCCESS } from 'src/app/global';
import { PATH_CREATE_PATIENT, PATH_PATIENTS } from '../patients.global';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { DialogService } from 'primeng/dynamicdialog';
import { toObservable } from '@angular/core/rxjs-interop';

import { ButtonDirective } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { PatientTableComponent } from '../patient-table/patient-table.component';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { COLLECTION_PATIENTS } from 'src/app/calvin-emr-shared/global';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { PrimeTemplate } from 'primeng/api';
import { getUserFromSnapshot } from 'src/app/calvin-emr-shared/utils/user.util';

interface AdminWithShareStatus extends User {
  alreadyHasPatient?: boolean;
}

@Component({
  selector: 'app-patient-list-page',
  templateUrl: './patient-list-page.component.html',
  styleUrls: ['./patient-list-page.component.scss'],
  providers: [DialogService],
  standalone: true,
  imports: [
    ButtonDirective,
    RouterLink,
    CheckboxModule,
    FormsModule,
    PatientTableComponent,
    ConfirmationDialogComponent,
    DialogModule,
    TableModule,
    PrimeTemplate,
],
})
export class PatientListPageComponent extends PageComponent implements OnInit, OnDestroy {
  /**
   * Array of all patients held by the current user.
   */
  patients: Patient[] = [];

  selectedPatients: Patient[] = [];

  // Used for importing patients
  importErrors: string[] = [];

  showImportPatientsDialog = false;
  showDeletePatientsDialog = false;
  confirmDeletePatients = false;
  showSharePatientDialog = false;
  otherAdministrators: AdminWithShareStatus[] = [];
  selectedAdminsForSharing: User[] = [];
  isLoadingAdmins = false;

  /**
   * Path local constant to direct to "Create Patient" page.
   */
  createPatientPath = `/${PATH_PATIENTS}/${PATH_CREATE_PATIENT}`;

  private router = inject(Router);
  private userService = inject(UserService);
  private patientService = inject(PatientService);
  private dialogService = inject(DialogService);
  private toastService = inject(ToastService);

  constructor() {
    super();

    this.currentUserSub = toObservable(this.authStore.user).subscribe((user) => {
      if (user && user.id) {
        this.userService.getUser(user.id).subscribe((snapshot) => {
          this.currentUser = getUserFromSnapshot(snapshot);
          this.getPatients();
        });
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Unsubscribes from the currentUser$ Observable.
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.currentUserSub.unsubscribe();
  }

  /**
   * Get each patient in the user's patient list and add
   * resulting data to the "patients" array.
   */
  getPatients() {
    this.startLoading();
    this.patientService.getPatientsForUser(this.currentUser).subscribe((patients: Patient[]) => {
      this.patients = patients;
      this.stopLoading();
    });
  }

  openImportPatientsDialog() {
    this.showImportPatientsDialog = true;
  }

  /**
   * Open up the dialog to confirm deleting patients.
   */
  openDeletePatientsDialog() {
    this.showDeletePatientsDialog = true;
  }

  onCloseDeletePatientsDialog(didConfirm: boolean) {
    if (didConfirm) {
      this.deletePatients();
    } else {
      this.confirmDeletePatients = false;
    }
  }

  deletePatients() {
    this.startLoading();
    this.toastService.clear();
    this.selectedPatients.forEach((patient) => {
      this.userService.deletePatientIdFromAllUsers(patient.id);

      this.patientService.deletePatient(patient.id, COLLECTION_PATIENTS).subscribe(() => {
        this.patients = this.patients.filter((p) => p.id !== patient.id);
        this.toastService.addOne({
          severity: SUCCESS,
          summary: 'Success',
          detail: `Deleted patient ${patient.id}!`,
        });
        this.stopLoading();
        this.selectedPatients.pop();
      });
    });
  }

  /**
   * Open share patients dialog
   */
  async openSharePatientsDialog() {
    if (this.selectedPatients.length === 0) {
      return;
    }

    this.selectedAdminsForSharing = [];
    this.isLoadingAdmins = true;
    this.showSharePatientDialog = true;

    try {
      await this.fetchOtherAdministrators();
    } catch (err: any) {
      this.toastService.clearAndAddOne({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load administrators',
      });
    } finally {
      this.isLoadingAdmins = false;
    }
  }

  /**
   * Fetch other administrators
   */
  async fetchOtherAdministrators() {
    const allAdmins = await this.userService.getAllAdministrators();
    const currentUserId = this.userService.getCurrentUserId();
    const selectedPatientIds = this.selectedPatients.map((p) => p.id);

    this.otherAdministrators = allAdmins
      .filter((admin) => admin.id !== currentUserId)
      .map((admin) => {
        // Check if admin already has ANY of the selected patients
        const hasAnyPatient = selectedPatientIds.some(
          (patientId) => admin.patientIds?.includes(patientId) ?? false
        );
        const adminWithStatus: AdminWithShareStatus = admin;
        adminWithStatus.alreadyHasPatient = hasAnyPatient;
        return adminWithStatus;
      });
  }

  /**
   * Share selected patients with selected administrators
   */
  async sharePatientsWithAdmins() {
    if (this.selectedPatients.length === 0 || this.selectedAdminsForSharing.length === 0) {
      return;
    }

    try {
      const updatePromises = [];

      for (const admin of this.selectedAdminsForSharing) {
        for (const patient of this.selectedPatients) {
          updatePromises.push(this.userService.addPatientToAdministrator(admin.id, patient.id));
        }
      }

      await Promise.all(updatePromises);

      this.toastService.clearAndAddOne({
        severity: 'success',
        summary: 'Success',
        detail: `${this.selectedPatients.length} patient(s) shared with ${this.selectedAdminsForSharing.length} administrator(s)`,
      });

      this.showSharePatientDialog = false;
      this.selectedAdminsForSharing = [];
    } catch (err: any) {
      this.toastService.clearAndAddOne({
        severity: 'error',
        summary: 'Error',
        detail: err?.message || 'Failed to share patients',
      });
    }
  }

  get didSelectPatients(): boolean {
    return this.selectedPatients.length > 0;
  }
}
